/**
 * MenuService
 * Handles menu-related API calls to Drupal backend
 * Routes through FastAPI proxy for authentication
 */
import { proxyService } from './proxy.service';
import { getBaseUrl } from '@core/config/axios.config';
import { logApiRequest } from '@utils/logger.utils';

/**
 * Drupal Menu Item Response (with nested items support)
 */
export interface DrupalMenuItem {
  key: string;
  title: string;
  description: string | null;
  uri: string;
  alias: string | null;
  external: boolean;
  absolute: string;
  relative: string;
  existing: boolean;
  weight: string;
  expanded: boolean;
  enabled: boolean;
  uuid: string | null;
  options: Record<string, any>;
  below?: DrupalMenuItem[]; // Nested menu items for footer
}

/**
 * Transformed Menu Item for frontend use
 */
export interface MenuItem {
  key: string;
  title: string;
  href: string;
  isExternal: boolean;
  isHome: boolean;
  children?: MenuItem[]; // Nested menu items for footer
}

/**
 * Transform Drupal menu item to frontend MenuItem
 */
const transformMenuItem = (drupalItem: DrupalMenuItem, includeChildren = false): MenuItem => {
  // Check if it's the home page
  const isHome = drupalItem.key === 'standard.front_page' || drupalItem.relative === '/';
  
  // Determine href
  let href = drupalItem.relative || drupalItem.absolute || '/';
  
  // If it's an external link, use absolute URL
  if (drupalItem.external && drupalItem.absolute) {
    href = drupalItem.absolute;
  } else if (drupalItem.alias) {
    // Use alias if available
    href = drupalItem.alias.startsWith('/') ? drupalItem.alias : `/${drupalItem.alias}`;
  } else if (drupalItem.relative && drupalItem.relative !== '/') {
    // Use relative path (could be anchor link like #about)
    href = drupalItem.relative;
  } else if (isHome) {
    // Home page
    href = '/';
  }
  
  // Handle anchor links (e.g., #about) - keep them as-is for scroll behavior
  // These will be handled by the Header component's handleLinkClick
  
  const menuItem: MenuItem = {
    key: drupalItem.key,
    title: drupalItem.title,
    href,
    isExternal: drupalItem.external || false,
    isHome,
  };

  // Include nested children if available and requested
  if (includeChildren && drupalItem.below && Array.isArray(drupalItem.below)) {
    menuItem.children = drupalItem.below
      .filter((item) => item.enabled)
      .map((item) => transformMenuItem(item, false))
      .sort((a, b) => {
        // Sort by weight if available
        const aWeight = parseInt(drupalItem.below?.find((i) => i.key === a.key)?.weight || '0', 10);
        const bWeight = parseInt(drupalItem.below?.find((i) => i.key === b.key)?.weight || '0', 10);
        return aWeight - bWeight;
      });
  }
  
  return menuItem;
};

class MenuService {
  /**
   * Get main menu items
   * Endpoint: /api/menu_items/main?_format=json&max_depth=1
   */
  async getMainMenu(): Promise<MenuItem[]> {
    const url = `/api/menu_items/main?_format=json&max_depth=1`;
    logApiRequest('GET', `${getBaseUrl()}${url}`);
    
    const response = await proxyService.get<DrupalMenuItem[]>(url);
    const menuItems = Array.isArray(response.data) ? response.data : [];
    
    // Filter only enabled items and transform
    return menuItems
      .filter((item) => item.enabled)
      .map((item) => transformMenuItem(item, false))
      .sort((_a, _b) => {
        // Sort by weight if available (items are already sorted by Drupal)
        return 0;
      });
  }

  /**
   * Get footer menu items (with nested children)
   * Endpoint: /api/menu_items/footer?_format=json&max_depth=2
   */
  async getFooterMenu(): Promise<MenuItem[]> {
    const url = `/api/menu_items/footer?_format=json&max_depth=2`;
    logApiRequest('GET', `${getBaseUrl()}${url}`);
    
    const response = await proxyService.get<DrupalMenuItem[]>(url);
    const menuItems = Array.isArray(response.data) ? response.data : [];
    
    // Filter only enabled items and transform with children
    return menuItems
      .filter((item) => item.enabled)
      .map((item) => transformMenuItem(item, true))
      .sort((a, b) => {
        // Sort by weight (lower weight = higher priority)
        const aItem = menuItems.find((i) => i.key === a.key);
        const bItem = menuItems.find((i) => i.key === b.key);
        const aWeight = parseInt(aItem?.weight || '0', 10);
        const bWeight = parseInt(bItem?.weight || '0', 10);
        return aWeight - bWeight;
      });
  }
}

export const menuService = new MenuService();
export default menuService;

